/**
 * Single-active overlay registry used by dropdowns and pickers.
 * Opening one overlay closes any previously registered overlay.
 * When a host (and optional detached panel) is supplied, mousedown/focus
 * outside those roots closes the overlay — panels appended to `document.body`
 * must be registered via `panelElement` / `setActivePanel`.
 */
export class DropdownToggleHelper {
  private static activeId: string | null = null;
  private static closeActive: (() => void) | null = null;
  private static activeHost: HTMLElement | null = null;
  private static activePanel: HTMLElement | null = null;
  private static outsideListenersBound = false;

  private static readonly onOutsidePointerDown = (event: Event): void => {
    DropdownToggleHelper.closeIfOutside(event.target);
  };

  private static readonly onOutsideFocusIn = (event: Event): void => {
    DropdownToggleHelper.closeIfOutside(event.target);
  };

  /**
   * Mark `id` as the only open overlay. Optionally supply `onCloseElsewhere`
   * so local open state can be cleared when another overlay claims focus.
   * Pass `hostElement` (trigger root) and optional `panelElement` (body portal)
   * so outside-click ignores both.
   */
  static open(
    id: string,
    onCloseElsewhere?: () => void,
    hostElement?: HTMLElement,
    panelElement?: HTMLElement | null
  ): void {
    if (this.activeId !== id) {
      this.invokePreviousCloser();
    }
    this.activeId = id;
    this.closeActive = onCloseElsewhere ?? null;
    this.activeHost = hostElement ?? null;
    this.activePanel = panelElement ?? null;
    this.syncOutsideListeners();
  }

  /** Update the detached panel root after it is created / moved to body. */
  static setActivePanel(id: string, panelElement: HTMLElement | null): void {
    if (this.activeId === id) {
      this.activePanel = panelElement;
    }
  }

  static close(id: string): void {
    if (this.activeId === id) {
      this.clearActive();
    }
  }

  /**
   * Toggle open state for id-based consumers (e.g. wi-multi-select).
   * Returns true when the overlay should now be open.
   */
  static toggle(id: string, onCloseElsewhere?: () => void, hostElement?: HTMLElement): boolean {
    if (this.activeId === id) {
      this.clearActive();
      return false;
    }

    this.open(id, onCloseElsewhere, hostElement);
    return true;
  }

  static isOpen(id: string): boolean {
    return this.activeId === id;
  }

  static closeAll(): void {
    this.invokePreviousCloser();
    this.clearActive();
  }

  static hasOpen(): boolean {
    return !!this.activeId;
  }

  private static closeIfOutside(target: EventTarget | null): void {
    if (!this.hasOpen()) {
      return;
    }
    if (!(this.activeHost || this.activePanel)) {
      return;
    }
    if (target instanceof Node && this.isInsideOverlay(target)) {
      return;
    }
    this.closeAll();
  }

  private static isInsideOverlay(target: Node): boolean {
    return !!(this.activeHost?.contains(target) || this.activePanel?.contains(target));
  }

  private static clearActive(): void {
    this.activeId = null;
    this.closeActive = null;
    this.activeHost = null;
    this.activePanel = null;
    this.syncOutsideListeners();
  }

  private static syncOutsideListeners(): void {
    if (typeof document === 'undefined') {
      return;
    }

    const shouldListen = !!(this.activeHost || this.activePanel);
    if (shouldListen && !this.outsideListenersBound) {
      document.addEventListener('mousedown', this.onOutsidePointerDown, true);
      document.addEventListener('focusin', this.onOutsideFocusIn, true);
      this.outsideListenersBound = true;
      return;
    }

    if (!shouldListen && this.outsideListenersBound) {
      document.removeEventListener('mousedown', this.onOutsidePointerDown, true);
      document.removeEventListener('focusin', this.onOutsideFocusIn, true);
      this.outsideListenersBound = false;
    }
  }

  private static invokePreviousCloser(): void {
    if (!this.closeActive) {
      return;
    }
    const closePrev = this.closeActive;
    this.closeActive = null;
    this.activeId = null;
    closePrev();
  }
}
